import { IWorkflowNode } from "motw_core/Entity/IWorkflowNode";
import { ESharedFunction } from "motw_core/Enums/ESharedFunction";
import { workflowRepository } from "motw_core/Repositories/WorkflowRepository";
import * as React from "react";
import { WithAnimatedProgressBarAsync } from "source/app/presentation/Header/ProgressBar/LevelProgressBar/WithAnimatedProgressBar";
import { IRootNodeFormState } from "source/app/presentation/MainPanel/Workflows/RootNodeName/Helpers/IRootNodeFormState";
import { EditNodeDetails } from "source/app/presentation/MainPanel/Workflows/RootNodeName/NodeDetails/EditNodeDetails";
import {
    FloatingTools,
    useHasFloatingToolTargets,
} from "source/app/presentation/Shared/FloatingTools/FloatingTools";
import ClientDataProvider from "source/app/services/External/ClientDataProvider/ClientDataProvider";
import { ShareState } from "source/app/services/UI/State/ShareState";
import Render, { RenderDiv } from "../../../Shared/Render";
import { DisplayNodeDetails } from "./DisplayNodeDetails";
import { GetFontSize } from "./GetFontSize";
import { GetMergedFormState } from "./Heleprs/GetMergedFormState";
import * as styles from "./RootNodeName.css";
import { CanReadInBookOfMindAsync } from "./RootNodeNameTools/ReadInBookOfMind/CanReadInBookOfMindAsync";
import {
    IWorkflowsFloatingToolInputs,
    workflowsFloatingTools,
} from "./WorkflowsFloatingTools/WorkflowsFloatingTools";
import { StyleClass } from "source/app/presentation/Shared/Styles/CSS";

export function RootNodeName({
    rootNode,
    selectedNodeCount,
    isMultiSelectMode,
}: {
    rootNode: IWorkflowNode;
    selectedNodeCount: number;
    isMultiSelectMode: boolean;
}) {
    const mergedFormState: IRootNodeFormState = GetMergedFormState(rootNode);

    const [state, setState] = React.useState<IRootNodeFormState>(
        GetMergedFormState(rootNode)
    );
    const [canReadInBookOfMind, setCanReadInBookOfMind] =
        React.useState(false);
    const hasWorkflowNodes = useHasFloatingToolTargets(
        "[data-workflow-node]"
    );

    React.useEffect(() => {
        const isCurrentlyEditing = state.showEditControls;

        if (isCurrentlyEditing) {
            console.log(
                `[Workflows] prevented update of this workflow node while editing`
            );
            return;
        }

        const mergedFormState: IRootNodeFormState =
            GetMergedFormState(rootNode);

        WithAnimatedProgressBarAsync(
            () =>
                workflowRepository
                    .GetParentNodeAsync(ClientDataProvider, rootNode)
                    .then((parentNode) => {
                        setState({
                            ...mergedFormState,
                            parentNode: parentNode!,
                        });
                    }),
            `RootNodeName_GetParentNodeAsync`
        );

        setState({ ...mergedFormState });
    }, [rootNode]);

    React.useEffect(() => {
        let isCurrentRootNode = true;
        setCanReadInBookOfMind(false);

        CanReadInBookOfMindAsync(rootNode).then((canRead) => {
            if (isCurrentRootNode) {
                setCanReadInBookOfMind(canRead);
            }
        });

        return () => {
            isCurrentRootNode = false;
        };
    }, [rootNode]);

    ShareState(ESharedFunction.RootNodeName_state, state, setState);

    const fontSize = GetFontSize(rootNode);

    return (
        <div className={styles.rootNodeName} style={{ fontSize }}>
            <RenderDiv id="existingValues" when={!state.showEditControls}>
                <DisplayNodeDetails
                    {...{
                        rootNode,
                        state,
                        setState,
                    }}
                />
            </RenderDiv>
            <Render
                id="nodeDetails"
                when={state.showEditControls}
                withClasses={StyleClass(`nodeDetailsContainer`, {
                    width: `100%`,
                })}
            >
                <EditNodeDetails
                    state={state}
                    loadedFormState={mergedFormState}
                    setState={setState}
                    {...{ rootNode }}
                />
            </Render>
            {FloatingTools<IWorkflowsFloatingToolInputs>(
                {
                    rootNode,
                    canReadInBookOfMind,
                    hasWorkflowNodes,
                    selectedNodeCount,
                    isMultiSelectMode,
                },
                workflowsFloatingTools
            )}
        </div>
    );
}
